import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Upload, CheckCircle, Clock, Eye, Pencil, Trash, PaperPlaneTilt,
  ArrowRight, Warning
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const statusConfig = {
  draft: { label: "Draft", color: "bg-zinc-500", textColor: "text-zinc-400", icon: Pencil },
  ready_for_review: { label: "Ready for Review", color: "bg-blue-500", textColor: "text-blue-400", icon: Eye },
  approved: { label: "Approved", color: "bg-emerald-500", textColor: "text-emerald-400", icon: CheckCircle },
  uploading: { label: "Uploading", color: "bg-violet-500", textColor: "text-violet-400", icon: Upload },
  published: { label: "Published", color: "bg-cyan-500", textColor: "text-cyan-400", icon: CheckCircle },
};

const StatusColumn = ({ title, status, lessons, onStatusChange, onPublish }) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div className="flex-1 min-w-[280px]">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-3 h-3 rounded-full ${config.color}`} />
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">{title}</h3>
        <span className="text-xs text-zinc-500">({lessons.length})</span>
      </div>

      <div className="space-y-3">
        {lessons.map((lesson) => (
          <motion.div
            key={lesson.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-sm font-medium text-white line-clamp-2">{lesson.title}</h4>
            </div>
            
            <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
              <span>{lesson.chapter || 'No chapter'}</span>
              <span>•</span>
              <span>{lesson.duration_minutes} min</span>
            </div>

            <div className="flex items-center gap-2">
              {status !== 'published' && (
                <Select
                  value={status}
                  onValueChange={(newStatus) => onStatusChange(lesson.id, newStatus)}
                >
                  <SelectTrigger className="h-8 text-xs bg-zinc-800 border-white/10 rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="ready_for_review">Ready for Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              {status === 'approved' && (
                <Button
                  onClick={() => onPublish(lesson.id)}
                  size="sm"
                  data-testid={`publish-lesson-${lesson.id}`}
                  className="h-8 px-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-semibold"
                >
                  <PaperPlaneTilt size={14} className="mr-1" />
                  Publish
                </Button>
              )}
              
              {status === 'published' && (
                <span className="text-xs text-cyan-400 flex items-center gap-1">
                  <CheckCircle size={14} />
                  Live on Whop
                </span>
              )}
            </div>
          </motion.div>
        ))}
        
        {lessons.length === 0 && (
          <div className="text-center py-8 text-zinc-600 text-sm">
            No lessons in this stage
          </div>
        )}
      </div>
    </div>
  );
};

export default function AcademyPublisher() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const res = await axios.get(`${API}/lessons`);
      setLessons(res.data);
    } catch (err) {
      toast.error("Failed to fetch lessons");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (lessonId, newStatus) => {
    try {
      await axios.put(`${API}/lessons/${lessonId}`, { status: newStatus });
      setLessons(lessons.map(l => l.id === lessonId ? { ...l, status: newStatus } : l));
      toast.success("Status updated!");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handlePublish = async (lessonId) => {
    try {
      // Call the real Whop publish endpoint
      setLessons(lessons.map(l => l.id === lessonId ? { ...l, status: 'uploading' } : l));
      
      const res = await axios.post(`${API}/lessons/${lessonId}/publish-to-whop`);
      
      setLessons(lessons.map(l => l.id === lessonId ? { ...l, status: 'published' } : l));
      toast.success(res.data.message || "Lesson published to Whop Academy!");
    } catch (err) {
      setLessons(lessons.map(l => l.id === lessonId ? { ...l, status: 'approved' } : l));
      toast.error(err.response?.data?.detail || "Failed to publish to Whop");
    }
  };

  // Group lessons by status
  const groupedLessons = {
    draft: lessons.filter(l => l.status === 'draft'),
    ready_for_review: lessons.filter(l => l.status === 'ready_for_review'),
    approved: lessons.filter(l => l.status === 'approved'),
    uploading: lessons.filter(l => l.status === 'uploading'),
    published: lessons.filter(l => l.status === 'published'),
  };

  const totalLessons = lessons.length;
  const publishedCount = groupedLessons.published.length;
  const pendingCount = groupedLessons.ready_for_review.length + groupedLessons.approved.length;

  return (
    <div data-testid="academy-publisher">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Academy Publisher
          </h1>
          <p className="text-zinc-400">Manage your lesson publishing queue</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Total Lessons</p>
          <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {totalLessons}
          </p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Published</p>
          <p className="text-3xl font-bold text-cyan-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {publishedCount}
          </p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Pending Review</p>
          <p className="text-3xl font-bold text-amber-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {pendingCount}
          </p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Drafts</p>
          <p className="text-3xl font-bold text-zinc-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {groupedLessons.draft.length}
          </p>
        </div>
      </div>

      {/* Publishing Flow Indicator */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 mb-8">
        <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4">Publishing Flow</h3>
        <div className="flex items-center justify-between">
          {Object.entries(statusConfig).map(([key, config], index) => {
            const Icon = config.icon;
            const count = groupedLessons[key]?.length || 0;
            return (
              <React.Fragment key={key}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full ${config.color}/20 flex items-center justify-center mb-2`}>
                    <Icon size={20} className={config.textColor} />
                  </div>
                  <p className="text-xs text-zinc-400">{config.label}</p>
                  <p className="text-lg font-bold text-white">{count}</p>
                </div>
                {index < Object.entries(statusConfig).length - 1 && (
                  <ArrowRight size={20} className="text-zinc-700" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Kanban Board */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex gap-6 overflow-x-auto pb-4">
          <StatusColumn
            title="Drafts"
            status="draft"
            lessons={groupedLessons.draft}
            onStatusChange={handleStatusChange}
            onPublish={handlePublish}
          />
          <StatusColumn
            title="Ready for Review"
            status="ready_for_review"
            lessons={groupedLessons.ready_for_review}
            onStatusChange={handleStatusChange}
            onPublish={handlePublish}
          />
          <StatusColumn
            title="Approved"
            status="approved"
            lessons={groupedLessons.approved}
            onStatusChange={handleStatusChange}
            onPublish={handlePublish}
          />
          <StatusColumn
            title="Published"
            status="published"
            lessons={groupedLessons.published}
            onStatusChange={handleStatusChange}
            onPublish={handlePublish}
          />
        </div>
      )}

      {/* Integration Notice */}
      <div className="mt-8 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <CheckCircle size={24} className="text-emerald-500 shrink-0" weight="fill" />
          <div>
            <h4 className="text-white font-semibold mb-1">Whop Connected</h4>
            <p className="text-sm text-zinc-400">
              Your Whop Academy is connected! Lessons will be published to your "Founding Membership" product.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
